import { useEffect, useRef, type RefObject } from 'react';
import { useDispatch } from 'react-redux';
import { type Crop } from 'react-image-crop';

import StorytellerUtils from 'lib/StorytellerUtils';
import { EnvironmentFactory } from 'StorytellerEnvironment';
import { useRichTextEditor } from 'editor/hooks/useRichTextEditor';
import { useStoryLayout } from 'editor/hooks/useStoryLayout';
import { updateBlockComponent } from 'store/reducers/StoryReducer';
import { type ReactRendererProps } from '../shared/ComponentRegistry';
import { addNoopenerToBlankLinks } from './add-noopener-to-blank-links';

interface AuthorImage {
  alt?: string;
  crop?: Crop;
  documentId: string | null;
  url: string;
}

interface AuthorValue {
  blurb?: string;
  image?: AuthorImage;
}

const AuthorBlurb = ({
  editorRef,
  editMode,
  blurbHtml,
}: {
  editorRef: RefObject<HTMLDivElement>;
  editMode: boolean;
  blurbHtml: string;
}) => {
  if (!editMode) {
    return (
      <div
        data-testid="component-author-blurb"
        className="author-blurb col10 component-html typeset squire-formatted remove-top-margin"
        dangerouslySetInnerHTML={{ __html: addNoopenerToBlankLinks(blurbHtml) }}
      />
    );
  }

  return (
    <div ref={editorRef} data-testid="component-author-blurb" className="author-blurb col10 component-html">
      <div className="component-blinder hidden" />
    </div>
  );
};

/**
 * Sample componentData:
 *  {
 *    type: "author",
 *    value: {
 *      blurb: '<p>This author is in such and such department</p>',
 *      image: {
 *        documentId: '1234',
 *        url: 'https://bucket-name.s3.amazonaws.com/uploads/random/image.jpg'
 *      }
 *    }
 *  }
 */
export const ComponentAuthor = ({
  componentData,
  theme,
  blockId,
  componentIndex,
  editMode,
}: ReactRendererProps) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<AuthorValue>({});

  const typeClasses = StorytellerUtils.typeToClassesForComponentType(componentData.type);
  const value = (componentData?.value ?? {}) as AuthorValue;
  const altText = value?.image?.alt ?? '';
  const env = EnvironmentFactory();
  const defaultImageUrl = env.IMAGES?.COVER_IMAGE_ICON ?? '/images/large-profile.png';
  const imageUrl = value.image?.url ?? defaultImageUrl;
  const blurbHtml = value.blurb ?? '';

  // Keep current value accessible in event handlers without stale closures
  valueRef.current = value;

  const layout = useStoryLayout();
  const { ref: editorRef, editor } = useRichTextEditor({
    content: blurbHtml,
    theme,
    layout,
    extraContentClasses: ['remove-top-margin'],
  });

  // Dispatch blurb content changes to Redux when the rich text editor fires
  useEffect(() => {
    if (!editMode || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const handleContentChange = (event: Event) => {
      event.stopPropagation();
      const blurbContent = (event as CustomEvent<{ content: string }>).detail.content;
      const newValue = { ...valueRef.current, blurb: blurbContent };
      dispatch(updateBlockComponent({ blockId, componentIndex, type: 'author', value: newValue }));
    };

    container.addEventListener('rich-text-editor::content-change', handleContentChange);
    return () => container.removeEventListener('rich-text-editor::content-change', handleContentChange);
  }, [editMode, blockId, componentIndex, dispatch]);

  // Adjust container height when the editor or image height changes
  useEffect(() => {
    if (!editMode || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const handleHeightChange = () => {
      const imageEl = container.querySelector<HTMLElement>('.author-image');
      const blurbEl = container.querySelector<HTMLElement>('.author-blurb');
      const imageHeight = imageEl?.offsetHeight ?? 0;
      const negativeTextPadding = blurbEl ? parseInt(getComputedStyle(blurbEl).marginTop, 10) || 0 : 0;

      if (editor) {
        const editorHeight = editor.getContentHeight();
        const iframe = container.querySelector<HTMLIFrameElement>('.component-html iframe');
        if (iframe) {
          iframe.style.height = `${editorHeight}px`;
        }
        container.style.height = `${Math.max(editorHeight + negativeTextPadding, imageHeight)}px`;
      } else {
        container.style.height = `${imageHeight}px`;
      }
    };

    container.addEventListener('rich-text-editor::height-change', handleHeightChange);
    container.addEventListener('component::height-change', handleHeightChange);
    return () => {
      container.removeEventListener('rich-text-editor::height-change', handleHeightChange);
      container.removeEventListener('component::height-change', handleHeightChange);
    };
  }, [editMode, editor]);

  return (
    <div ref={containerRef} className={`component ${typeClasses}`} data-url={imageUrl}>
      <div className="author-image col2 component-image" data-testid="component-author-image">
        <img src={imageUrl} alt={altText} />
      </div>
      <AuthorBlurb editorRef={editorRef} editMode={editMode} blurbHtml={blurbHtml} />
    </div>
  );
};
